// 261012 E1-P3 — hàm THUẦN của cột "Nhận dạng hình ảnh" (chặng ②, bảng Nhân vật / Bối cảnh). KHÔNG import antd / react
// (jest của react-scripts không load được antd v6). `VisualIdentityCell` / `VisualIdentityBar` / `useVisualIdentity` chỉ vẽ + gọi API.
// Hợp đồng dữ liệu: spec `261012-epic1-phase3.md` D2 (PATCH) + D7 (GET `profileView`, `limits.maxChars`).
import type {
  VisualEntityKind, VisualErrorCode, VisualJobResult, VisualProfileView, VisualRunBody, VisualRunningJob,
} from '../../../types/adaptation';
import { friendlyApiMessage } from './jobErrorText';
import type { DangerCopyView } from './workbenchView';

// ─── Trạng thái ô ──────────────────────────────────────────────────────────

export interface VisualCtx {
  /** Chặng ② đang khoá. */
  stageLocked: boolean;
  /** Cờ ghi DÙNG CHUNG (`working` của SkeletonStage) — đang ghi tên / khung xương / nhận dạng. */
  working: boolean;
  /** Job `FILM_VISUAL_IDENTITY` đang chạy (của phiên này hoặc BE báo). */
  jobRunning: boolean;
  /** Vỏ báo bận vì việc khác (`ctx.busy` của WorkbenchContext). */
  shellBusy: boolean;
  /** Summary nhận dạng đã tải. */
  loaded: boolean;
  loadFailed: boolean;
}

export type VisualTone = 'ai' | 'user' | 'keep' | 'warn' | 'mut';
export interface VisualTag { key: string; label: string; tone: VisualTone }
export type VisualAction = 'edit' | 'lock' | 'redo' | 'clear' | 'propose';

export interface VisualCellView {
  /** `loading`: chưa có summary · `failed`: tải lỗi · `empty`: chưa có profile · `filled`: có `text`. */
  phase: 'loading' | 'failed' | 'empty' | 'filled';
  /** Nhãn trạng thái ngắn: "Chưa có đề xuất" / "AI đề xuất" / "Đã sửa tay" / "Không tải được nhận dạng". */
  label: string;
  tone: VisualTone;
  hasProfile: boolean;
  text: string;
  tags: VisualTag[];
  /** Dòng chú nhỏ (lỗi thời, Bank thắng). */
  notes: string[];
  /** Gợi ý khi chưa có profile ("Film chỉ thấy tên…"). */
  hint?: string;
  /** Dòng khối Film SẼ đọc; `null` khi không in được hoặc Bank thắng (khối bỏ dòng). */
  blockLine: string | null;
  canEdit: boolean;
  canLock: boolean;
  canRedo: boolean;
  canClear: boolean;
  /** Chưa có profile ⇒ "Đề xuất" (chạy AI cho riêng dòng này). */
  canPropose: boolean;
  /** Lý do khoá CHUNG (chặng khoá / job chạy / đang ghi / chưa tải); `undefined` khi rảnh. */
  reasonDisabled?: string;
  /** Lý do khoá từng nút (chung hoặc riêng như "bỏ Giữ mới xoá được"); `undefined` khi nút bật. */
  actionReason: Partial<Record<VisualAction, string>>;
}

export const VISUAL_JOB_RUNNING_REASON = 'Đang đề xuất nhận dạng ở nền';
const STAGE_LOCKED = 'Bước đang khoá';
const NOT_LOADED = 'Chưa tải được nhận dạng';
const WORKING = 'Đang lưu — đợi xong rồi thử lại';
const SHELL_BUSY = 'Đang có tác vụ chạy';

const hasProfileText = (v: VisualProfileView | null | undefined): v is VisualProfileView =>
  !!v && typeof v.text === 'string' && v.text.trim() !== '';

/** `baseVersion` gửi kèm khi ghi: chưa có profile ⇒ `"none"` (D2). */
export const baseVersionOf = (v: VisualProfileView | null | undefined): string =>
  (v && typeof v.version === 'string' && v.version) || 'none';

const blockReason = (ctx: VisualCtx): string | undefined => {
  if (ctx.stageLocked) return STAGE_LOCKED;
  if (!ctx.loaded) return NOT_LOADED;
  if (ctx.jobRunning) return VISUAL_JOB_RUNNING_REASON;
  if (ctx.working) return WORKING;
  if (ctx.shellBusy) return SHELL_BUSY;
  return undefined;
};

const EMPTY_HINT: Record<VisualEntityKind, string> = {
  character: 'Film chỉ thấy tên, tự đoán tuổi/dáng.',
  location: 'Film chỉ thấy tên bối cảnh, tự đoán cảnh.',
};
const BANK_NOTE: Record<VisualEntityKind, string> = {
  character: 'Truyện đã có hồ sơ chọn diễn viên cho nhân vật này; Film dùng hồ sơ đó, không dùng mô tả này.',
  location: 'Truyện đã có mô tả bối cảnh này trong Bank; Film dùng mô tả đó, không dùng mô tả này.',
};
const STALE_NOTE = 'Chính sách bản địa hoá / tên hiển thị đã đổi sau khi sinh — nên đọc lại.';

export const cellState = (view: VisualProfileView | null | undefined, ctx: VisualCtx, kind: VisualEntityKind = 'character'): VisualCellView => {
  const none = { canEdit: false, canLock: false, canRedo: false, canClear: false, canPropose: false };
  if (!ctx.loaded) {
    const failed = ctx.loadFailed;
    return {
      phase: failed ? 'failed' : 'loading', label: failed ? 'Không tải được nhận dạng' : '…', tone: 'mut', hasProfile: false, text: '',
      tags: [], notes: [], blockLine: null, ...none, reasonDisabled: NOT_LOADED,
      actionReason: { edit: NOT_LOADED, lock: NOT_LOADED, redo: NOT_LOADED, clear: NOT_LOADED, propose: NOT_LOADED },
    };
  }
  const blocked = blockReason(ctx);
  const allBlocked = (): Partial<Record<VisualAction, string>> =>
    ({ edit: blocked, lock: blocked, redo: blocked, clear: blocked, propose: blocked });

  if (!hasProfileText(view)) {
    return {
      phase: 'empty', label: 'Chưa có đề xuất', tone: 'mut', hasProfile: false, text: '', tags: [], notes: [], hint: EMPTY_HINT[kind], blockLine: null,
      canEdit: !blocked, canLock: false, canRedo: false, canClear: false, canPropose: !blocked,
      reasonDisabled: blocked,
      actionReason: blocked ? allBlocked() : {},
    };
  }

  const user = view.status === 'USER_EDITED';
  const tags: VisualTag[] = [user ? { key: 'status', label: 'Đã sửa tay', tone: 'user' } : { key: 'status', label: 'AI đề xuất', tone: 'ai' }];
  if (view.source === 'SOURCE_MENTION') {
    tags.push({ key: 'source', label: view.evidenceCount > 0 ? `Nguyên tác nhắc tới (${view.evidenceCount} câu)` : 'Nguyên tác nhắc tới', tone: 'mut' });
  } else if (view.source === 'AI_INFERRED') {
    tags.push({ key: 'source', label: 'AI suy luận (nguyên tác không tả)', tone: 'warn' });
  }
  if (view.locked) tags.push({ key: 'keep', label: 'Giữ', tone: 'keep' });
  const notes: string[] = [];
  if (view.stale) { tags.push({ key: 'stale', label: 'Có thể lỗi thời', tone: 'warn' }); notes.push(STALE_NOTE); }
  const bankWins = view.bank === 'WINS';
  if (bankWins) { tags.push({ key: 'bank', label: 'Bank đã có — khối bỏ dòng này', tone: 'warn' }); notes.push(BANK_NOTE[kind]); }

  // Lý do riêng của từng nút (chỉ khi không bị khoá chung).
  const redoReason = view.locked ? 'Đang Giữ — bỏ Giữ rồi mới đề xuất lại' : user ? 'Đã sửa tay — xoá rồi mới đề xuất lại' : undefined;
  const clearReason = view.locked ? 'Đang Giữ — bỏ Giữ mới xoá được' : undefined;
  const actionReason: Partial<Record<VisualAction, string>> = blocked ? allBlocked() : {};
  if (!blocked) {
    if (redoReason) actionReason.redo = redoReason;
    if (clearReason) actionReason.clear = clearReason;
  }
  return {
    phase: 'filled', label: user ? 'Đã sửa tay' : 'AI đề xuất', tone: user ? 'user' : 'ai', hasProfile: true, text: view.text, tags, notes,
    blockLine: bankWins ? null : (view.blockLine ?? null),
    canEdit: !blocked, canLock: !blocked, canRedo: !blocked && !redoReason, canClear: !blocked && !clearReason, canPropose: false,
    reasonDisabled: blocked, actionReason,
  };
};

// ─── Ô sửa tay ─────────────────────────────────────────────────────────────

export interface EditValidation {
  /** Đếm bằng `Array.from` (ký tự ngoài BMP đếm 1) trên bản đã chuẩn hoá một dòng. */
  count: number;
  max: number | null;
  empty: boolean;
  over: boolean;
  ok: boolean;
  /** Bản một dòng (gộp mọi khoảng trắng/xuống dòng) — thứ nên gửi lên. */
  normalized: string;
  message?: string;
}

/** `maxChars` lấy từ `summary.limits.maxChars` — thiếu (summary chưa tải) ⇒ KHÔNG cho lưu (không hardcode giới hạn). */
export const editValidation = (text: string, maxChars: number | null | undefined): EditValidation => {
  const normalized = String(text ?? '').replace(/\s+/g, ' ').trim();
  const count = Array.from(normalized).length;
  const max = typeof maxChars === 'number' && maxChars > 0 ? maxChars : null;
  const empty = count === 0;
  const over = max !== null && count > max;
  const ok = !empty && !over && max !== null;
  const message = empty ? 'Chưa nhập mô tả' : over ? `Quá dài: ${count}/${max} ký tự` : undefined;
  return { count, max, empty, over, ok, normalized, ...(message ? { message } : {}) };
};

/** Xem thử dòng khối Film sẽ in (gần đúng — dòng thật do server tính, `blockLine`). Cùng khuôn `visualContextBlock`: `Nhãn: mô tả`, bối cảnh có `[Setting] `. */
export const previewBlockLine = (kind: VisualEntityKind, label: string, text: string): string => {
  const desc = String(text ?? '').replace(/\s+/g, ' ').trim();
  const lab = String(label ?? '').replace(/[:：\t\n\r\v\f]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!desc || !lab) return '';
  return `${lab}: ${kind === 'location' ? '[Setting] ' : ''}${desc}`;
};

// ─── Chạy AI (tốn AI) ────────────────────────────────────────────────────

export type RunScope = 'missing' | 'one';

export interface RunConfirmInput {
  scope: RunScope;
  /** Số thực thể sẽ được đề xuất. */
  count: number;
  /** `scope:'one'`: dòng đã có profile ⇒ đề xuất lại (mặc định `true` cho 'one'). */
  redo?: boolean;
  name?: string;
  /** Loại dòng (`scope:'one'`) — cảnh báo "đổi dân tộc/tuổi" chỉ áp cho nhân vật. Mặc định `character`. */
  kind?: VisualEntityKind;
}

/** Các hộp hỏi lại theo thứ tự — chỉ còn một hộp xác nhận (261015: bỏ hộp "truyện thật"). */
export const runConfirmCopy = (i: RunConfirmInput): { steps: DangerCopyView[] } => {
  const redo = i.scope === 'one' && i.redo !== false;
  const who = i.scope === 'one' ? (i.name?.trim() || 'dòng này') : `${i.count} mục còn thiếu`;
  const lines = [
    `Chạy AI ${i.scope === 'one' ? (redo ? 'đề xuất lại' : 'đề xuất') + ' cho' : 'đề xuất cho'} ${who}.`,
    ...(redo ? [i.kind === 'location' ? 'Kết quả mới có thể khác lần trước — chi tiết bối cảnh có thể đổi.' : 'Kết quả mới có thể khác lần trước — có thể đổi dân tộc/tuổi.'] : []),
    'Ô đã sửa tay hoặc đã Giữ sẽ không bị đổi.',
  ];
  const steps: DangerCopyView[] = [{
    title: redo ? `Đề xuất lại ${who}?` : i.scope === 'one' ? `Đề xuất nhận dạng cho ${who}?` : `Tạo đề xuất cho ${i.count} mục còn thiếu?`,
    content: lines.join(' '), okText: redo ? 'Đề xuất lại' : 'Chạy AI', danger: false,
  }];
  return { steps };
};

export interface RunBodyInput {
  storyId: string;
  scope: RunScope;
  /** `scope:'one'` bắt buộc. */
  id?: string;
  redo?: boolean;
}

/** Body `POST visual-identity`. */
export const buildRunBody = (i: RunBodyInput): VisualRunBody => {
  if (i.scope === 'one' && !i.id) throw new Error('buildRunBody: scope "one" cần id');
  const body: VisualRunBody = i.scope === 'one'
    ? { storyId: i.storyId, redo: i.redo !== false, onlyIds: [i.id as string] }
    : { storyId: i.storyId, redo: false };
  return body;
};

// ─── Lỗi + kết quả job ─────────────────────────────────────────────────────

export interface ErrorTextCtx { term?: string; maxChars?: number | null; message?: string }

export const errorText = (code: string | undefined, c: ErrorTextCtx = {}): { text: string } => {
  const server = c.message ? friendlyApiMessage(c.message, '') : '';
  switch (code as VisualErrorCode | undefined) {
    case 'EMPTY_TEXT': return { text: 'Chưa nhập mô tả.' };
    case 'TOO_LONG': return { text: `Mô tả quá dài${c.maxChars ? ` (tối đa ${c.maxChars} ký tự)` : ''}.` };
    case 'NAME_LEAK': return { text: `Mô tả còn chứa tên gốc${c.term ? ` “${c.term}”` : ''} — dùng tên trong phim hoặc bỏ tên.` };
    case 'NO_PROFILE': return { text: 'Chưa có mô tả để Giữ — bấm Tự viết hoặc Đề xuất trước.' };
    case 'LOCKED': return { text: 'Đang Giữ — bỏ Giữ rồi mới xoá được.' };
    case 'PROFILE_CHANGED': return { text: 'Mô tả vừa được cập nhật ở nơi khác — đã tải lại, bấm Sửa để xem bản mới.' };
    case 'JOB_RUNNING': return { text: server ? `${server} — đợi xong rồi thử lại.` : 'Đang có tác vụ chạy ở nền — đợi xong rồi thử lại.' };
    case 'OUTLINE_CHANGED': return { text: 'Khung xương vừa đổi ở nơi khác — đã tải lại, thử lại.' };
    default: return { text: friendlyApiMessage(c.message, 'Không thực hiện được') };
  }
};

/** Mã lỗi mà trạng thái đang hiện đã cũ ⇒ phải tải lại summary; lỗi nhập (rỗng/dài/lộ tên) giữ ô sửa mở. */
const RELOAD_CODES: ReadonlySet<string> = new Set(['PROFILE_CHANGED', 'LOCKED', 'NO_PROFILE', 'JOB_RUNNING', 'OUTLINE_CHANGED']);

/** Đọc lỗi axios của PATCH/POST nhận dạng. */
export const visualErrorOf = (err: unknown, maxChars?: number | null): { code: string | undefined; text: string; reload: boolean } => {
  const d = (err as { response?: { data?: { code?: unknown; message?: unknown; term?: unknown } } } | null | undefined)?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : undefined;
  const { text } = errorText(code, {
    term: typeof d?.term === 'string' ? d.term : undefined,
    maxChars,
    message: typeof d?.message === 'string' ? d.message : undefined,
  });
  return { code, text, reload: !!code && RELOAD_CODES.has(code) };
};

/** Toast khi job đề xuất xong (`task.detail.result` phẳng của `buildVisualIdentityJobResult`). */
export const jobDoneText = (r: VisualJobResult | null | undefined): string => {
  if (!r || typeof r.appliedCount !== 'number') return 'Đã xong đề xuất nhận dạng';
  if (r.appliedCount <= 0) {
    const sk = typeof r.skippedCount === 'number' && r.skippedCount > 0 ? ` (bỏ qua ${r.skippedCount})` : '';
    return `Không thêm đề xuất nào${sk}`;
  }
  return `Đã thêm ${r.appliedCount} đề xuất`;
};

/** Job `FILM_VISUAL_IDENTITY` đang chạy trong `summary.runningJobs` ⇒ id để nhận nuôi sau tải lại trang. */
export const pickVisualJobId = (jobs: ReadonlyArray<Pick<VisualRunningJob, 'id' | 'type'>> | null | undefined): string | null =>
  (jobs ?? []).find((j) => j?.type === 'FILM_VISUAL_IDENTITY')?.id ?? null;

// ─── Thanh đầu bảng ────────────────────────────────────────────────────────

export interface BarChip { key: string; label: string; tone: VisualTone }
export interface BarView {
  total: number; filled: number; missing: number; edited: number; kept: number; stale: number;
  chips: BarChip[];
  runLabel: string;
  canRun: boolean;
  runReason?: string;
}

/** `views` = `profileView` của mọi thực thể trong tab (null = chưa có). `eligible` = `summary.counts.eligible` (N mục còn thiếu CẢ TRUYỆN — POST không giới hạn id, D9). */
export const barView = (views: ReadonlyArray<VisualProfileView | null | undefined>, ctx: VisualCtx, eligible: number): BarView => {
  const have = views.filter(hasProfileText);
  const total = views.length;
  const filled = have.length;
  const missing = total - filled;
  const edited = have.filter((v) => v.status === 'USER_EDITED').length;
  const kept = have.filter((v) => v.locked).length;
  const stale = have.filter((v) => v.stale).length;
  const chips: BarChip[] = [{ key: 'filled', label: `Đủ ${filled}/${total}`, tone: 'mut' }];
  if (missing > 0) chips.push({ key: 'missing', label: `Thiếu ${missing}`, tone: 'warn' });
  if (edited > 0) chips.push({ key: 'edited', label: `Sửa tay ${edited}`, tone: 'user' });
  if (kept > 0) chips.push({ key: 'kept', label: `Giữ ${kept}`, tone: 'keep' });
  if (stale > 0) chips.push({ key: 'stale', label: `Có thể lỗi thời ${stale}`, tone: 'warn' });
  const n = Math.max(0, Math.floor(eligible || 0));
  const blocked = blockReason(ctx);
  const runReason = blocked ?? (n === 0 ? 'Không có mục nào thiếu' : undefined);
  return { total, filled, missing, edited, kept, stale, chips, runLabel: `Tạo đề xuất cho mục còn thiếu (${n})`, canRun: !runReason, ...(runReason ? { runReason } : {}) };
};
